<template>
  <div class="content">
    <Background>
      <Loading v-if="loading" isCenter isFull />
      <FriendlierCard v-else>
        <div class="padded-block">
          <div class="logo">
            <LocaleAwareLogo variant="primary" color="cloud" />
          </div>

          <div class="donation-confirmation-section">
            <div class="title">Confirm Your Donation</div>

            <div v-if="charity" class="charity-details">
              <div class="charity-logo">
                <img :src="charity.logo" :alt="charity.charityName" />
              </div>
              <h2>{{ charity.charityName }}</h2>
              <p>{{ charity.descriptionLang?.en || charity.description }}</p>
              <p><a :href="charity.websiteUrl" target="_blank" rel="noopener noreferrer">Learn More</a></p>
            </div>

            <div class="donation-form">
              <div class="form-group">
                <label>Email (Optional):</label>
                <input id="notify-email" name="email" v-model="notifyEmail" type="email"
                  placeholder="Be notified of your donation" autocomplete="email" inputmode="email" autocapitalize="off"
                  spellcheck="false" class="input" @input="emailChange" @blur="emailBlur" />
                <p v-if="emailSuggestion" class="email-suggestion">
                  Did you mean
                  <button type="button" class="email-suggestion-link" @click="applyEmailSuggestion">{{ emailSuggestion
                  }}</button>?
                </p>
              </div>

              <div class="error" v-if="error">{{ error }}</div>

              <div class="button-container">
                <button class="button primary-button" @click="confirmDonation" :disabled="donationLoading">
                  <Loading v-if="donationLoading" isCenter isContained />
                  <span v-else>Confirm Donation</span>
                </button>
              </div>
            </div>

            <div class="back-button">
              <button class="button secondary-button" @click="goBack" :disabled="donationLoading">
                Back to Charity Selection
              </button>
            </div>
          </div>
        </div>
      </FriendlierCard>
    </Background>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";

import {
  Loading,
  Background,
  FriendlierCard,
  LocaleAwareLogo,
} from "@/revamp/components";
import { useFirebase } from "@/revamp/composables";
import { getSuggestedEmail, logger, toQueryString } from "@/revamp/utils";

const loading = ref(true);
const donationLoading = ref(false);
const charity = ref(null);
const notifyEmail = ref("");
const emailSuggestion = ref(null);
const error = ref("");

const APIService = useFirebase();
const Router = useRouter();
const route = useRoute();

let codeDisplay = "";
let codeUrl = "";
let source = "";
let platform = "";
let codeForm = "";
let pin = "";

onMounted(async () => {
  notifyEmail.value = toQueryString(route.query.email);
  codeDisplay = toQueryString(route.query.codeDisplay);
  codeUrl = toQueryString(route.query.codeUrl);
  source = toQueryString(route.query.source);
  platform = toQueryString(route.query.platform);
  codeForm = toQueryString(route.query.codeForm);
  pin = toQueryString(route.query.pin);
  if (!codeDisplay && !codeUrl) {
    logger.error("No container code provided in the query parameters.");
    Router.push({ name: "Return" });
    return;
  }
  if (!source || !platform || !codeForm) {
    logger.error("Missing required query parameters: source, platform, or codeForm.");
    Router.push({ name: "Return" });
    return;
  }

  if (route.query.charity) {
    charity.value = JSON.parse(decodeURIComponent(toQueryString(route.query.charity)));
  }
  // If no charity, redirect back main screen
  if (!charity.value) {
    Router.push({ name: "Return" });
    return;
  }
  window.history.replaceState({}, document.title, "/donation-confirmation");

  loading.value = false;
});

function emailChange(e) {
  emailSuggestion.value = null;
  notifyEmail.value = e.target.value.trim();
  e.target.value = notifyEmail.value;
}

function emailBlur() {
  emailSuggestion.value = getSuggestedEmail(notifyEmail.value);
}

function applyEmailSuggestion() {
  if (!emailSuggestion.value) return;
  notifyEmail.value = emailSuggestion.value;
  emailSuggestion.value = null;
}

async function confirmDonation() {
  donationLoading.value = true;
  error.value = "";

  try {
    const language = navigator.language || navigator.userLanguage || "en";
    let responseCode = 0;
    if (codeUrl) {
      const apiResponse = await APIService.claimCodeByUrlQuickClaim(
        codeUrl,
        notifyEmail.value || undefined,
        notifyEmail.value ? language : undefined,
        charity.value.id,
        {
          platform: platform,
          source: source,
          codeForm: codeForm,
        },
      );
      if (!apiResponse) {
        throw new Error("Failed to create donation");
      }
      responseCode = apiResponse.error ? apiResponse.code : 0;
    } else {
      // Call backend to create anonymous donation
      const apiResponse = await APIService.createAnonymousDonation({
        code: codeDisplay,
        charityID: charity.value.id,
        platform: platform,
        source: source,
        codeForm: codeForm,
        notifyEmail: notifyEmail.value || undefined,
        emailLanguage: notifyEmail.value ? language : undefined, // only English for now
        pin: pin || undefined
      });
      if (!apiResponse) {
        throw new Error("Failed to create donation. ");
      }
      responseCode = apiResponse.error ? apiResponse.code : 0;
    }

    if (responseCode === 30004) {
      throw new Error("Container already claimed");
    } else if (responseCode === 30019) {
      throw new Error("Container is not available");
    } else if (responseCode !== 0) {
      throw new Error("Failed to create donation");
    }

    // Show success and redirect
    Router.replace({
      name: "DonationSuccess",
      query: {
        charityName: charity.value.charityName,
      }
    });
  } catch (err) {
    logger.error(`DonationConfirmation.confirmDonation: Failed to process donation. ${err.toString()}`);
    error.value = err.message || "Failed to process donation. Please try again.";
  } finally {
    donationLoading.value = false;
  }
}

function goBack() {
  Router.replace({
    name: "CharitySelection",
    query: {
      codeDisplay: codeDisplay,
      codeUrl: codeUrl,
      source: source,
      codeForm: codeForm,
      platform: platform,
      email: notifyEmail.value || undefined,
      pin: pin || undefined
    }
  });
}
</script>

<style scoped lang="scss">
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";
@import "@/revamp/assets/scss/variables.scss";

.donation-confirmation-section {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-top: 15px;

  .title {
    font-size: 1.5rem;
    color: $sunflower;
    width: 100%;
    margin-bottom: 30px;
    text-align: center;
    font-weight: bold;
  }

  input[disabled] {
    background-color: $cloud !important;
    color: $denim !important;
  }
}

.email-suggestion {
  margin-top: 0.5rem;
  color: #4a4a4a;
}

.email-suggestion-link {
  appearance: none;
  border: none;
  background: none;
  color: #0048b4;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.charity-details {
  text-align: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .charity-logo {
    margin-bottom: 15px;

    img {
      max-height: 80px;
      max-width: 150px;
      object-fit: contain;
    }
  }

  h2 {
    color: $denim;
    font-size: 1.3rem;
    margin-bottom: 10px;
    font-weight: bold;
  }

  p {
    color: black;
    font-size: 1rem;
    line-height: 1.4;
  }

  a {
    color: $denim;
    text-decoration: none;
    font-weight: bold;

    &:hover {
      text-decoration: underline;
    }
  }
}

.donation-form {
  margin-bottom: 30px;

  .form-group {
    margin-bottom: 20px;

    label {
      display: block;
      color: $sunflower;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 1rem;
    }

    .input {
      width: 100%;
      padding: 12px;
      border-radius: 10px;
      border: 1px solid $cloud;
      font-size: 1rem;

      &:focus {
        outline: none;
        border-color: $sunflower;
      }
    }
  }

  .error {
    color: red;
    margin-bottom: 15px;
    text-align: center;
    font-size: 0.9rem;
  }

  .button-container {
    text-align: center;
    margin-top: 20px;

    .button {
      padding: 15px 30px;
      border-radius: 10px;
      border: none;
      font-weight: bold;
      font-size: 1.1rem;
      cursor: pointer;
      transition: all 0.3s ease;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .primary-button {
      background-color: $sunflower;
      color: $denim;

      &:hover:not(:disabled) {
        background-color: darken($sunflower, 10%);
      }
    }
  }
}

.back-button {
  text-align: center;

  .button {
    padding: 12px 24px;
    border-radius: 10px;
    border: none;
    font-weight: bold;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .secondary-button {
    background-color: $cloud;
    color: $denim;

    &:hover {
      background-color: darken($cloud, 10%);
    }
  }
}

.logo {
  width: 100%;
  text-align: center;
  margin-bottom: 15px;
}

.logo img {
  max-width: 150px;
  height: auto;
}
</style>